import { ConfigStateService, PermissionService, RoutesService, eLayoutType } from '@abp/ng.core';
import { eChatPolicyNames } from '@volo/abp.ng.chat/config';
import { eIdentityRouteNames } from '@volo/abp.ng.identity/config';
import { inject, provideAppInitializer } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PermissionConstants } from 'src/core/constants/permission.constant';
import { CAMPAIGNS_ROUTE_NAME } from 'src/core/constants/theme/navigable-parent-routes.constants';
import {
  WALLBOARD_PRESENTATION_ROUTE_PARAM,
  WALLBOARD_PRESENTATION_ROUTE_PATH,
  WALLBOARD_SHARED_PRESENTATION_ROUTE_PATH,
} from './features/wallboard/constants/wallboard-presentation-route.constants';
export const APP_ROUTE_NAMES = ['::Menu:Dashboard', '::Menu:AgentDesktop'] as const;

export const APP_ROUTE_PROVIDER = [
  provideAppInitializer(() => {
    configureRoutes();
  }),
  provideAppInitializer(() => hideAdministrationByPermission()),
];
function configureRoutes() {
  const routes = inject(RoutesService);
  routes.add([
    {
      path: '/dashboard',
      name: '::Menu:Dashboard',
      iconClass: 'fas fa-chart-line',
      order: 1,
      layout: eLayoutType.application,
      // Either tab grants the entry: the page is a Dashboard | Wallboard shell, and the two permissions
      // are independent siblings on the server, so a wallboard-only role must still see the menu item.
      requiredPolicy: PermissionConstants.anyDashboardTabPermission,
    },
    {
      path: '/agent-desktop',
      name: '::Menu:AgentDesktop',
      iconClass: 'fas fa-headset',
      order: 2,
      layout: eLayoutType.application,
      requiredPolicy: PermissionConstants.canViewAgentDesktop,
    },
    {
      path: '/contacts',
      name: '::Menu:Contacts',
      iconClass: 'fas fa-address-book',
      order: 3,
      layout: eLayoutType.application,
      requiredPolicy: PermissionConstants.canViewContacts,
    },
    {
      path: '/conversations',
      name: '::Menu:Conversations',
      iconClass: 'fas fa-comments',
      order: 4,
      layout: eLayoutType.application,
      requiredPolicy: PermissionConstants.canViewConversations,
    },
    {
      // A page AND a group: AcxNavbarService keeps its link, so a click opens Campaigns and expands it.
      path: '/campaigns',
      name: CAMPAIGNS_ROUTE_NAME,
      iconClass: 'fas fa-bullhorn',
      order: 5,
      layout: eLayoutType.application,
      requiredPolicy: PermissionConstants.canViewCampaigns,
    },
    {
      path: '/campaigns/health',
      name: '::Menu:CampaignPipelineHealth',
      parentName: CAMPAIGNS_ROUTE_NAME,
      order: 1,
      layout: eLayoutType.application,
      requiredPolicy: PermissionConstants.canViewCampaignPipelineHealthOverview,
    },
    {
      path: '/administration/agent-management',
      name: 'AgentManagement::Menu:AgentManagement',
      parentName: eIdentityRouteNames.IdentityManagement,
      order: 25,
      layout: eLayoutType.application,
      requiredPolicy: PermissionConstants.agentManagement,
    },
    {
      path: '/no-access',
      name: '::NoAccess:Title',
      layout: eLayoutType.empty,
      invisible: true,
    },
    {
      // Full-screen wallboard presentation (`/dashboard/wallboard/present/:id`): no menu, no top bar.
      // DynamicLayoutComponent resolves the layout via `findRoute`, which strips trailing segments until
      // a RoutesService path matches, so this prefix covers every presentation id — and without it the
      // walk would stop at '/dashboard' and render the application layout.
      path: `/${WALLBOARD_PRESENTATION_ROUTE_PATH.replace(`/:${WALLBOARD_PRESENTATION_ROUTE_PARAM}`, '')}`,
      name: 'Dashboard::WallboardPresentations',
      layout: eLayoutType.empty,
      invisible: true,
    },
    {
      // Login-less shared presentation (`/wallboard/shared#<token>`): no menu, no top bar. The token is in
      // the fragment, which ABP's `getRoutePath` drops (it reads only the URL tree's path segments), so the
      // path matches this entry exactly — without it `findRoute` would walk up to '/' and render the
      // application layout around a wall display. The name is only RoutesService's key: the entry is
      // invisible (no menu item) and its empty layout has no breadcrumb, so nothing ever translates it —
      // hence a plain name, not a key-like one. The browser title comes from the Angular route's own
      // `title` in `app.routes.ts`.
      path: `/${WALLBOARD_SHARED_PRESENTATION_ROUTE_PATH}`,
      name: 'WallboardSharedPresentation',
      layout: eLayoutType.empty,
      invisible: true,
    },
    {
      path: '/notifications',
      name: 'AgenticCX::Notification:Title',
      layout: eLayoutType.application,
      invisible: true,
    },
  ]);
}

async function hideAdministrationByPermission(): Promise<void> {
  const configState = inject(ConfigStateService);
  const routesService = inject(RoutesService);
  const permissionService = inject(PermissionService);

  await firstValueFrom(configState.getAll$().pipe(filter(config => !!config?.currentUser)));

  const hasAnyAdminPermission = permissionService.getGrantedPolicy(
    PermissionConstants.anyAdministrationPermission
  );

  if (!hasAnyAdminPermission) {
    routesService.remove(['AbpUiNavigation::Menu:Administration']);
  }
}
